'use client';

import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  Tooltip as MTooltip,
  Chip,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { H2, H3, H4, Paragraph } from '../Typography';
import {
  format,
  formatDistanceToNow,
  isValid,
} from 'date-fns';
import { currencyFormatter, percentFormatter } from '@web/utils/currency';
import NextImage from 'next/image';
import { useState } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { FlexBox } from '@web/components/flex-box';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { manageShopURL } from '@web/utils/link';
import { useAnalytics } from '@web/context/analytics';
import SellIcon from '@mui/icons-material/Sell';
import DumbLink from '@web/components/DumbLink';
import SellerImage from '@web/components/SellerImage';
import { useParams } from 'next/navigation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  TimeRangeControls,
  TimeRange,
} from '@web/app/components/TimeRangeControls';
import { filterByTimeRange } from '@web/utils/timeRange';
import PriceRating from './PriceRating';
import { consolidatePriceHistory } from '@web/utils/priceHistory';
import useSWR from 'swr';

const getPriceStats = (
  priceHistory: { base_price: number; created_at: string }[]
) => {
  if (!priceHistory?.length) return null;

  const prices = priceHistory.map((p) => p.base_price);
  return {
    allTimeHigh: Math.max(...prices),
    allTimeLow: Math.min(...prices),
    average: prices.reduce((a, b) => a + b, 0) / prices.length,
    current: prices[prices.length - 1],
  };
};

export default function OfferTable({ offers, historicalOffers, oz, lastUpdatedAt, timeRange, onTimeRangeChange }) {
  const [showAll, setShowAll] = useState(false);
  const { trackOfferClick } = useAnalytics();
  const perPage = 10;

  const params = useParams();

  const { data: offersHistory } = useSWR(
    () => params?.slug && `/api/product/${params.slug}?timeRange=${timeRange}`,
    (url) => fetch(url).then(res => res.json())
  );

  const handleClick = (url, seller_id) => {
    trackOfferClick(url, seller_id);
    window.open(manageShopURL(url), '_blank');
  };

  const OfferCard = ({ offer, isHistorical = false }) => {
    const ozDeals = oz?.filter((oz) => oz.link === offer.seller_product_url);
    const priceHistory = offersHistory?.[offer.seller_product_url] || [];

    // Find last price that was different from current
    const currentPrice = priceHistory?.[priceHistory?.length - 1]?.base_price;
    const lastDifferentPrice = priceHistory
      ?.slice(0, -1)
      .reverse()
      .find((p) => p.base_price !== currentPrice)?.base_price;

    const price_off = lastDifferentPrice
      ? 1 - currentPrice / lastDifferentPrice
      : 0;

    // Check if stock status should be treated as "in stock"
    const isInStock = offer.stock_status?.toLowerCase().includes('in stock') ||
                      offer.stock_status?.toLowerCase().includes('see merchant');

    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        return (
          <Paragraph>
            {formatDistanceToNow(new Date(payload[0].payload.created_at))} ago :{' '}
            {currencyFormatter.format(payload[0].value)}
          </Paragraph>
        );
      }

      return null;
    };

    const priceStats = getPriceStats(priceHistory);

    return (
      <Card>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ py: 0.5, px: 1, minHeight: 68 }}
        >
          <Grid item xs={2}>
            <FlexBox alignItems={'center'} flex={1} gap={2}>
              <DumbLink
                href={`/shop/${offer.seller.name}`}
                target="_blank"
                prefetch={false}
              >
                <SellerImage seller_product_url={offer?.seller_product_url} />
              </DumbLink>
              <DumbLink
                href={`/shop/${offer.seller.name}`}
                target="_blank"
                prefetch={false}
              >
                <Typography color="secondary">{offer.seller.name}</Typography>
              </DumbLink>
            </FlexBox>
          </Grid>
          <Grid item xs={7.5} flex={1}>
            {/*{offer.seller_name.toLowerCase().includes('amazon') ? (*/}
            {/*  <Stack*/}
            {/*    direction="row"*/}
            {/*    justifyContent="center"*/}
            {/*    alignItems="center"*/}
            {/*  >*/}
            {/*    <Typography variant="body2">*/}
            {/*      Price history is not available for Amazon*/}
            {/*    </Typography>*/}
            {/*  </Stack>*/}
            {/*) : (*/}
            {priceHistory && (
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                <ResponsiveContainer width="70%" height={60}>
                  <LineChart data={priceHistory}>
                    <YAxis domain={['dataMin', 'dataMax']} hide />
                    <Line
                      type="monotone"
                      dot={(props): any => {
                        // If price never changed, don't show any dots
                        if (priceStats?.allTimeHigh === priceStats?.allTimeLow)
                          return null;

                        const isHighest =
                          props.value === priceStats?.allTimeHigh;
                        const isLowest = props.value === priceStats?.allTimeLow;

                        // Find last occurrence of high/low prices
                        const lastHighIndex = [...priceHistory]
                          .reverse()
                          .findIndex(
                            (p) => p.base_price === priceStats?.allTimeHigh
                          );
                        const lastLowIndex = [...priceHistory]
                          .reverse()
                          .findIndex(
                            (p) => p.base_price === priceStats?.allTimeLow
                          );

                        // Convert reverse index to normal index
                        const highIndex =
                          lastHighIndex === -1
                            ? -1
                            : priceHistory.length - 1 - lastHighIndex;
                        const lowIndex =
                          lastLowIndex === -1
                            ? -1
                            : priceHistory.length - 1 - lastLowIndex;

                        // Only show dot if it's the last occurrence of high/low
                        if (
                          props.index !== highIndex &&
                          props.index !== lowIndex
                        )
                          return null;

                        return (
                          <circle
                            key={props.index}
                            cx={props.cx}
                            cy={props.cy}
                            r={3}
                            fill={
                              props.value === priceStats?.allTimeHigh
                                ? '#d32f2f'
                                : '#2e7d32'
                            }
                            stroke="white"
                            strokeWidth={1}
                          />
                        );
                      }}
                      dataKey="base_price"
                      strokeWidth={2}
                      isAnimationActive={false}
                      stroke={
                        price_off === 0
                          ? '#8884d8'
                          : price_off > 0
                          ? 'green'
                          : 'red'
                      }
                    />
                    <Tooltip content={<CustomTooltip />} />
                  </LineChart>
                </ResponsiveContainer>
                <Stack spacing={0.5} ml={1}>
                  {price_off !== 0 && !isNaN(price_off) && (
                    <H4 color={price_off > 0 ? 'green' : 'red'}>
                      {percentFormatter.format(-price_off)}
                    </H4>
                  )}
                  {priceStats &&
                    priceStats.allTimeHigh !== priceStats.allTimeLow && (
                      <Stack>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#d32f2f',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight:
                              currentPrice === priceStats.allTimeHigh
                                ? 600
                                : 400,
                          }}
                        >
                          H: {currencyFormatter.format(priceStats.allTimeHigh)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#2e7d32',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight:
                              currentPrice === priceStats.allTimeLow
                                ? 600
                                : 400,
                          }}
                        >
                          L: {currencyFormatter.format(priceStats.allTimeLow)}
                        </Typography>
                      </Stack>
                    )}
                </Stack>
              </Stack>
            )}
          </Grid>
          <Grid item xs={2.5} alignItems={'center'}>
            <Stack direction="row" alignItems={'center'} spacing={2}>
              <Stack flex={1}>
                <H3>{currencyFormatter.format(offer.base_price)}</H3>
                {!offer.seller_product_url && (
                  <Chip
                    label="In store only"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                <Box>
                  {offer.coupon &&
                    offer.coupon.split(',').map((coupon, index) => (
                      <Chip
                        key={index}
                        label={
                          <Typography fontWeight={600}>{coupon}</Typography>
                        }
                        variant="outlined"
                        size="small"
                        icon={<SellIcon />}
                        sx={{
                          borderRadius: 2,
                        }}
                      />
                    ))}
                </Box>
              </Stack>
              <Stack spacing={1}>
                <Button
                  variant="contained"
                  noWrap
                  component={Typography}
                  color={isHistorical ? 'warning' : (isInStock ? 'primary' : 'warning')}
                  onClick={() =>
                    handleClick(
                      offer.seller_product_url,
                      offer.seller.seller_id
                    )
                  }
                >
                  {isHistorical
                    ? 'Out of Stock'
                    : isInStock
                    ? 'Go to store'
                    : offer.stock_status || 'Go to store'}
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        {(offer.product_title || offer.delivery_info) && (
          <>
            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              py={1}
              px={2}
            >
              {offer.product_title && (
                <Typography variant="body2" color="text.secondary">
                  {offer.product_title}
                </Typography>
              )}
              {offer.delivery_info && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  {offer.delivery_info}
                </Typography>
              )}
            </Stack>
          </>
        )}
        {ozDeals?.map((oz) => (
          <Box key={oz.node}>
            <Divider />
            <FlexBox
              component={DumbLink}
              href={`https://www.ozbargain.com.au/node/${oz.node}?utm_source=buywisely`}
              target="_blank"
              sx={{
                pl: 4,
                pr: 1,
                py: 1,
              }}
            >
              <NextImage
                src={`/images/oz.webp`}
                width={20}
                height={20}
                alt="ozbargain"
                style={{ objectFit: 'contain' }}
              />
              <Stack direction="row" gap={1} alignItems="center" px={2}>
                <Typography fontWeight="600" color="#4a4">
                  {oz.upvotes}
                </Typography>
                <Typography fontWeight="600" color="#c30">
                  {oz.downvotes}
                </Typography>
                {oz.status && (
                  <Chip
                    label={oz.status}
                    size="small"
                    color="error"
                    sx={{ fontWeight: 700 }}
                  />
                )}
              </Stack>
              <Typography color="secondary" variant="body2">
                {oz.title}
              </Typography>
            </FlexBox>
          </Box>
        ))}
      </Card>
    );
  };

  const consolidatedPrices = consolidatePriceHistory(offersHistory);

  return (
    <>
      <FlexBox alignItems="center" justifyContent="space-between" mt={2} mb={1}>
        <H2 fontSize="1.25rem">Price comparison</H2>
        <FlexBox alignItems="center" gap={2}>
          <TimeRangeControls value={timeRange} onChange={onTimeRangeChange} />
          <Paragraph>
            {lastUpdatedAt &&
              isValid(new Date(lastUpdatedAt)) &&
              `Last updated at ${format(
                new Date(lastUpdatedAt),
                'dd/MM/yyyy HH:mm:ss'
              )}`}
          </Paragraph>
        </FlexBox>
      </FlexBox>

      <Box mb={2}>
        <PriceRating
          currentPrice={consolidatedPrices.currentPrice}
          priceHistory={consolidatedPrices.history}
          timeRange={timeRange}
          viewport="desktop"
        />
      </Box>

      <Stack spacing={2}>
        {(showAll ? offers : offers.slice(0, perPage)).map(
          (offer, index) => (
            <OfferCard key={index} offer={offer} isHistorical={false} />
          )
        )}
      </Stack>
      {!showAll && perPage < offers.length && (
        <Button
          variant="text"
          size="large"
          fullWidth
          endIcon={<KeyboardArrowDownIcon />}
          onClick={() => setShowAll(true)}
        >
          View {offers.length - perPage} More
        </Button>
      )}

      {historicalOffers.length > 0 && (
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontSize={16} fontWeight={600}>
              See {historicalOffers.length} historical offers
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: 'rgba(0, 0, 0, .03)',
            }}
          >
            <Stack spacing={2}>
              {historicalOffers.map((offer, index) => (
                <OfferCard key={index} offer={offer} isHistorical={true} />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}
